import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';
import {AsyncPipe, CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {BehaviorSubject, Subscription} from 'rxjs';
import {Subtask} from 'src/app/core/model/task';
import {TasksService} from 'src/app/shared/services/tasks.service';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {ListViewsModule} from 'src/app/shared/list-views/list-views.module';
import * as uuid from 'uuid';

@Component({
  selector: 'subtask-list',
  standalone: true,
  imports: [CommonModule, DragDropModule, MatIconModule, OphButtonModule, ListViewsModule, AsyncPipe],
  templateUrl: './subtask-list.component.html',
  styleUrls: ['./subtask-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubtaskListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formArray: FormArray;
  @Input() itemBackground: 'light' | 'dark' = 'dark';

  @Output() subtaskIndexChange = new EventEmitter<number>();
  @Output() newSubtask = new EventEmitter<void>();

  hoverIndex$ = new BehaviorSubject<number>(null);
  subtasks$ = new BehaviorSubject<Subtask[]>([]);
  menuOptions = ['duplicate', 'delete'];

  subscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private tasksService: TasksService
  ) {}

  ngOnInit() {
    this.subscription = this.formArray.valueChanges.subscribe(subtasks => {
      this.subtasks$.next(subtasks);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.formArray && this.formArray) {
      this.subtasks$.next(this.formArray.getRawValue());
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDrop(event: CdkDragDrop<any[]>) {
    const subtasks = [...this.formArray.getRawValue()];
    moveItemInArray(subtasks, event.previousIndex, event.currentIndex);
    this.formArray.setValue(subtasks);
  }

  onMouseEnter(index: number) {
    this.hoverIndex$.next(index);
  }

  onMouseLeave(index: number) {
    this.hoverIndex$.next(null);
  }

  onMenuAction(action: string, i: number): void {
    switch (action) {
      case 'delete':
        this.onDelete(i);
        break;
      case 'duplicate':
        this.onDuplicate(i);
        break;
    }
  }

  onDuplicate(index: number) {
    const subtasks = this.formArray.getRawValue();
    const originalFormGroup = this.formArray.at(index) as FormGroup;

    const duplicateValue = {
      ...subtasks[index],
      _id: uuid.v4(),
      metricIds: [...(subtasks[index].metricIds || [])].map(m => ({
        metricId: m.metricId,
        readOnly: false,
      })),
    };

    // Create new form group with the same control configurations
    const duplicateFormGroup = this.fb.group({});
    Object.keys(originalFormGroup.controls).forEach(key => {
      const control = originalFormGroup.get(key);
      duplicateFormGroup.addControl(
        key,
        this.fb.control(duplicateValue[key], control.validator, control.asyncValidator)
      );
    });

    this.formArray.insert(index, duplicateFormGroup);
  }

  onDelete(index: number) {
    this.formArray.removeAt(index);
  }

  onEdit(index: number) {
    this.tasksService.setSubtaskEditIndex(index);
    this.subtaskIndexChange.emit(index);
  }
}
